import { AlertTriangle, Loader2 } from 'lucide-react';
import type { StatusMismatchResponse } from '@claro/shared';

interface StatusMismatchTableProps {
  data: StatusMismatchResponse | undefined;
  isLoading: boolean;
}

export function StatusMismatchTable({ data, isLoading }: StatusMismatchTableProps) {
  if (isLoading) {
    return (
      <div className="flex h-32 items-center justify-center rounded-lg border bg-card">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!data || data.total === 0) return null;

  return (
    <div className="rounded-lg border bg-card">
      <div className="flex items-center gap-2 border-b p-4">
        <AlertTriangle className="h-4 w-4 text-amber-500" />
        <h3 className="text-sm font-semibold">
          Ativas no PropertyDocs × Baixadas no Tributário ({data.total})
        </h3>
        <span className="text-xs text-muted-foreground">
          Unidades presentes no PD mas com CNPJ baixado na planilha do tributário
        </span>
      </div>

      <div className="max-h-96 overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-card">
            <tr className="border-b text-left text-xs text-muted-foreground">
              <th className="px-3 py-2">CNPJ</th>
              <th className="px-3 py-2">Unidade PD</th>
              <th className="px-3 py-2">Regional</th>
              <th className="px-3 py-2">Cidade PD</th>
              <th className="px-3 py-2">Identificação Tributário</th>
              <th className="px-3 py-2">Município Tributário</th>
              <th className="px-3 py-2 text-center">Match</th>
            </tr>
          </thead>
          <tbody>
            {data.data.map((unit) => (
              <tr key={unit.cnpj} className="border-b transition hover:bg-muted/50">
                <td className="px-3 py-2 tabular-nums text-xs">
                  {unit.cnpj.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5')}
                </td>
                <td className="px-3 py-2 font-medium">
                  {unit.pdUnitName || unit.pdUnitId}
                </td>
                <td className="px-3 py-2 text-xs">{unit.pdRegional || '—'}</td>
                <td className="px-3 py-2 text-xs">
                  {unit.pdCityName}{unit.pdStateName ? ` / ${unit.pdStateName}` : ''}
                </td>
                <td className="px-3 py-2 text-xs">{unit.taxIdentificacao || '—'}</td>
                <td className="px-3 py-2 text-xs">
                  {unit.taxMunicipio}{unit.taxUf ? ` / ${unit.taxUf}` : ''}
                </td>
                <td className="px-3 py-2 text-center">
                  <span className={`inline-flex rounded-md border px-2 py-0.5 text-[10px] font-medium ${
                    unit.matchMethod === 'doc-cnpj'
                      ? 'border-purple-500/30 bg-purple-500/15 text-purple-600 dark:text-purple-400'
                      : 'border-blue-500/30 bg-blue-500/15 text-blue-600 dark:text-blue-400'
                  }`}>
                    {unit.matchMethod === 'doc-cnpj' ? 'Alvará' : 'CNPJ'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
